import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sparkles, Mail, Lock } from "lucide-react";
import { useT } from "@/lib/i18n";
import LanguageSelector from "@/components/LanguageSelector";

export default function SaasLogin() {
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      setAuthToken(data.token);
      toast.success("Signed in");
      // Iter 111 · Platform admins skip onboarding + kiosk gate — send them
      // straight to the Control Panel.
      const role = (data.user?.role || "").toLowerCase();
      if (role === "admin" || role === "master") {
        nav("/admin/cpanel", { replace: true });
      } else if (data.tenant.onboarding_complete) {
        nav("/login", { replace: true });
      } else {
        nav("/onboarding", { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center p-6" data-testid="saas-login-page">
      <div className="absolute top-5 right-5 z-10">
        <LanguageSelector variant="light" />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-900 mb-4">
            <Sparkles className="h-7 w-7" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">KioskForce ERP</h1>
          <p className="text-slate-400 mt-2 text-sm">{t("saasLogin.subtitle")}</p>
        </div>

        <Card className="p-6 bg-white text-slate-900">
          <h2 className="text-xl font-bold mb-5">{t("saasLogin.title")}</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Mail className="h-3 w-3" /> {t("saasLogin.email")}
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="saas-login-email"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Lock className="h-3 w-3" /> {t("saasLogin.password")}
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="saas-login-password"
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 mt-2 bg-slate-900 hover:bg-slate-800 text-white font-bold"
              data-testid="saas-login-submit"
            >
              {busy ? "…" : t("saasLogin.signIn")}
            </Button>
          </form>

          <p className="text-xs text-slate-500 mt-5 text-center">
            {t("saasLogin.noAccount")}{" "}
            <Link to="/register" className="font-bold text-slate-900 underline" data-testid="link-to-register">
              {t("saasLogin.createOne")}
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
